import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const InviteUserModal = ({ isOpen, onClose, onInvite }) => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    department: '',
    role: '',
    sendWelcomeEmail: true,
    requirePasswordReset: true
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const departmentOptions = [
    { value: '', label: 'Select Department' },
    { value: 'Engineering', label: 'Engineering' },
    { value: 'Marketing', label: 'Marketing' },
    { value: 'Sales', label: 'Sales' },
    { value: 'HR', label: 'Human Resources' },
    { value: 'Finance', label: 'Finance' },
    { value: 'Operations', label: 'Operations' },
    { value: 'Legal', label: 'Legal' }
  ];

  const roleOptions = [
    { value: '', label: 'Select Role' },
    { value: 'Department Manager', label: 'Department Manager' },
    { value: 'Contributor', label: 'Contributor' },
    { value: 'Viewer', label: 'Viewer' },
    { value: 'Auditor', label: 'Auditor' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData?.department) {
      newErrors.department = 'Department is required';
    }

    if (!formData?.role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      onInvite(formData);
      
      // Reset form
      setFormData({
        email: '',
        name: '',
        department: '',
        role: '',
        sendWelcomeEmail: true,
        requirePasswordReset: true
      });
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Error inviting user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFormData({
        email: '',
        name: '',
        department: '',
        role: '',
        sendWelcomeEmail: true,
        requirePasswordReset: true
      });
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-300 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="UserPlus" size={20} color="white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Invite User</h2>
              <p className="text-sm text-muted-foreground">Add a new user to your organization</p>
            </div>
          </div>
          
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleClose}
            disabled={isLoading}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label="Email Address"
            type="email"
            placeholder="user@company.com"
            value={formData?.email}
            onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
            error={errors?.email}
            required
            disabled={isLoading}
          />

          <Input
            label="Full Name"
            type="text"
            placeholder="John Doe"
            value={formData?.name}
            onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
            error={errors?.name}
            required
            disabled={isLoading}
          />

          <Select
            label="Department"
            options={departmentOptions}
            value={formData?.department}
            onChange={(value) => setFormData({ ...formData, department: value })}
            error={errors?.department}
            required
            disabled={isLoading}
          />

          <Select
            label="Role"
            options={roleOptions}
            value={formData?.role}
            onChange={(value) => setFormData({ ...formData, role: value })}
            error={errors?.role}
            required
            disabled={isLoading}
          />

          <div className="space-y-3 pt-4 border-t border-border">
            <Checkbox
              label="Send welcome email with login instructions"
              checked={formData?.sendWelcomeEmail}
              onChange={(e) => setFormData({ ...formData, sendWelcomeEmail: e?.target?.checked })}
              disabled={isLoading}
            />

            <Checkbox
              label="Require password reset on first login"
              checked={formData?.requirePasswordReset}
              onChange={(e) => setFormData({ ...formData, requirePasswordReset: e?.target?.checked })}
              disabled={isLoading}
            />
          </div>

          <div className="flex space-x-3 pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              fullWidth
            >
              Cancel
            </Button>
            
            <Button
              type="submit"
              loading={isLoading}
              iconName="Send"
              iconPosition="left"
              iconSize={16}
              fullWidth
            >
              Send Invitation
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteUserModal;